'use client'

import AccountDetails from '../account/AccountDetails'
import { useEffect, useState } from 'react'
import { signout } from '@onclicks/sharedmedusa/medusaData/customer'
import { OrdersListDetails } from '../account/OrdersListDetails'
import { AddressesDetails } from '../account/AddressesDetails'
import { HttpTypes } from '@medusajs/types'
import { bagelFatOne } from '@/utilities/fontImport'
import { cn } from '@/utilities/ui'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useNumberItemsCart } from '@/providers/NumberItemsCartProvider'
import {
  faUser,
  faBoxOpen,
  faLocationDot,
  faArrowRightFromBracket,
} from '@fortawesome/free-solid-svg-icons'

export enum ACCOUNT_VIEW {
  ACCOUNT_DETAIL = 'account_detail',
  ORDER = 'order',
  ADDRESSES = 'address',
}

const AccountTemplate = ({
  customer,
  orders,
}: {
  customer: any
  orders: HttpTypes.StoreOrder[]
}) => {
  const [activeView, setActiveView] = useState<ACCOUNT_VIEW>(ACCOUNT_VIEW.ACCOUNT_DETAIL)
  const { setQuantity, refreshQuantity } = useNumberItemsCart()

  // AccountTemplate ne s'affiche que pour un client authentifié : son montage (y compris
  // juste après une connexion/inscription réussie, quand /account bascule du formulaire de
  // connexion vers cet écran) est un signal fiable pour resynchroniser le badge du panier
  // avec le panier réellement restauré côté serveur.
  useEffect(() => {
    refreshQuantity()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSignout = () => {
    setQuantity(0)
    signout()
  }

  const renderView = () => {
    switch (activeView) {
      case ACCOUNT_VIEW.ACCOUNT_DETAIL:
        return <AccountDetails customer={customer} />
      case ACCOUNT_VIEW.ORDER:
        return <OrdersListDetails orders={orders || []} />
      case ACCOUNT_VIEW.ADDRESSES:
        return <AddressesDetails customer={customer} />
      default:
        return <AccountDetails customer={customer} />
    }
  }

  const navItems = [
    { view: ACCOUNT_VIEW.ACCOUNT_DETAIL, label: 'Détails du compte', icon: faUser },
    { view: ACCOUNT_VIEW.ORDER, label: 'Commandes', icon: faBoxOpen },
    { view: ACCOUNT_VIEW.ADDRESSES, label: 'Adresses', icon: faLocationDot },
  ]

  const initials =
    `${customer?.first_name?.[0] || ''}${customer?.last_name?.[0] || ''}`.toUpperCase()

  return (
    <div className="w-full flex flex-col lg:flex-row justify-start my-10 lg:items-start px-4 lg:px-14 py-8 gap-8 mt-12 lg:mt-24">
      <nav className="w-full lg:w-68 shrink-0 rounded-4xl border border-white bg-white shadow-xl shadow-light-pink/40 p-6">
        <div className="flex items-center gap-3 pb-5 mb-4 border-b border-light-pink">
          <div className="flex h-13 w-13 shrink-0 items-center justify-center rounded-full bg-linear-to-r from-pink to-primary">
            <span className={cn('text-white text-sm', bagelFatOne.className)}>
              {initials ? initials : <FontAwesomeIcon icon={faUser} />}
            </span>
          </div>
          <div className="min-w-0">
            <p className="font-extrabold text-primary text-sm truncate">
              {customer?.first_name} {customer?.last_name}
            </p>
            <p className="font-semibold text-primary/50 text-xs truncate">{customer?.email}</p>
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          {navItems.map(({ view, label, icon }) => {
            const isActive = activeView === view

            return (
              <button
                key={view}
                onClick={() => setActiveView(view)}
                className={cn(
                  'flex items-center gap-3 rounded-full px-4 py-3 font-bold text-sm text-left transition-colors',
                  isActive
                    ? 'bg-linear-to-r from-secondary to-pink text-white shadow-md shadow-light-pink/60'
                    : 'text-primary/65 hover:bg-light-pink/20',
                )}
              >
                <FontAwesomeIcon icon={icon} className="w-4" />
                {label}
              </button>
            )
          })}

          <div className="h-px bg-light-pink my-2 mx-1" />

          <button
            onClick={handleSignout}
            className="flex items-center gap-3 rounded-full px-4 py-3 font-bold text-sm text-left text-light-red hover:bg-light-red/10 transition-colors"
          >
            <FontAwesomeIcon icon={faArrowRightFromBracket} className="w-4" />
            Se déconnecter
          </button>
        </div>
      </nav>

      <div className="flex-1 w-full">{renderView()}</div>
    </div>
  )
}

export default AccountTemplate
